/**
 * Image Generator Header Component
 *
 * Renders the top header bar of the image generator, including:
 * - Sidebar toggle button
 * - Title and info link
 * - Instance toggle buttons (1/2/3)
 * - Presets button
 */

import { useState, useCallback } from 'react';
import { Menu, Info, BookMarked, FolderOpen, Loader2, Eraser, Library } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import { ImageInstanceConfig, NUM_INSTANCES } from '../types/image-generator.types';
import type { ImageMode } from '../types/image-generator-modes';
import { ModeSelector } from './ModeSelector';
import { FavoriteButton } from '../../../components/FavoriteButton';
import { FiasAIToggle, useFiasAIContext } from '@/components/fias-ai';

export interface ImageGeneratorHeaderProps {
  isCollapsed: boolean;
  onToggleSidebar: () => void;
  enabledInstances: boolean[];
  minimizedInstances: boolean[];
  instanceConfigs: ImageInstanceConfig[];
  onToggleInstance: (idx: number) => void;
  onToggleMinimized: (idx: number) => void;
  canMinimize: (idx: number) => boolean;
  onOpenPresets: () => void;
  activeMode: ImageMode | null;
  modes: ImageMode[];
  onSelectMode: (mode: ImageMode) => void;
  onOpenModes: () => void;
  onClearAllImages: () => void;
}

export function ImageGeneratorHeader({
  isCollapsed,
  onToggleSidebar,
  enabledInstances,
  minimizedInstances,
  instanceConfigs,
  onToggleInstance,
  onToggleMinimized,
  canMinimize,
  onOpenPresets,
  activeMode,
  modes,
  onSelectMode,
  onOpenModes,
  onClearAllImages,
}: ImageGeneratorHeaderProps) {
  const navigate = useNavigate();
  const [isOpeningFolder, setIsOpeningFolder] = useState(false);
  const fiasAI = useFiasAIContext();

  const handleOpenAIImages = useCallback(async () => {
    setIsOpeningFolder(true);
    try {
      const { folder } = await api.ensureAIImagesFolder();
      navigate(`/my-data?folderId=${folder.folderId}`);
    } catch {
      // Folder may already exist, try navigating to my-data root
      navigate('/my-data');
    } finally {
      setIsOpeningFolder(false);
    }
  }, [navigate]);

  return (
    <div className="flex-shrink-0 border-b border-border bg-card/50 backdrop-blur-sm">
      <div className="px-4 py-3 flex items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="icon"
            onClick={onToggleSidebar}
            className={`h-11 w-11 flex-shrink-0 bg-gray-100 dark:bg-zinc-700 ${isCollapsed ? '' : 'invisible'}`}
            title="Open sidebar"
          >
            <Menu className="h-6 w-6" />
          </Button>
          <h1 className="text-lg font-semibold">Image Generator</h1>
          <FavoriteButton archeId="arche_image_generator" />
          <Link
            to="/marketplace/arches/arche_image_generator"
            className="text-muted-foreground hover:text-primary transition-colors"
            title="View arche details"
          >
            <Info className="h-6 w-6" />
          </Link>
          <FiasAIToggle onClick={fiasAI.togglePanel} isActive={fiasAI.isPanelOpen} />
          <Link
            to="/a/arche_image_generator/models"
            className="text-muted-foreground hover:text-primary transition-colors"
            title="Browse all models"
          >
            <Library className="h-6 w-6" />
          </Link>
          <button
            onClick={handleOpenAIImages}
            disabled={isOpeningFolder}
            className="ml-2 text-muted-foreground hover:text-primary transition-colors disabled:opacity-50"
            title="View AI Images folder"
          >
            {isOpeningFolder ? (
              <Loader2 className="h-6 w-6 animate-spin" />
            ) : (
              <FolderOpen className="h-6 w-6" />
            )}
          </button>
        </div>

        <div className="flex items-center gap-2 md:gap-4">
          <div className="flex items-center gap-1">
            {Array.from({ length: NUM_INSTANCES }, (_, idx) => {
              const enabled = enabledInstances[idx];
              const minimized = minimizedInstances[idx];

              // Disabled: ghost + dim, click to enable
              // Enabled + minimized: outline, click to expand
              // Enabled + expanded: default (solid), click to minimize
              const variant = !enabled ? 'ghost' : minimized ? 'outline' : 'default';
              const handleClick = () => {
                if (!enabled) {
                  onToggleInstance(idx);
                } else {
                  onToggleMinimized(idx);
                }
              };
              // Right-click to disable an enabled panel (only for optional panels 4+)
              const handleContextMenu = (e: React.MouseEvent) => {
                if (idx >= 3 && enabled) {
                  e.preventDefault();
                  onToggleInstance(idx);
                }
              };
              const isDisabled = enabled && !minimized && !canMinimize(idx);
              const title = !enabled
                ? `Enable panel ${idx + 1}`
                : idx >= 3
                  ? minimized
                    ? `Expand panel ${idx + 1} (right-click to disable)`
                    : `Collapse panel ${idx + 1} (right-click to disable)`
                  : minimized
                    ? `Expand ${instanceConfigs[idx].presetName}`
                    : `Collapse ${instanceConfigs[idx].presetName}`;

              return (
                <Button
                  key={idx}
                  variant={variant}
                  size="sm"
                  className={`h-8 w-8 p-0 text-xs ${!enabled ? 'opacity-40' : ''}`}
                  onClick={handleClick}
                  onContextMenu={handleContextMenu}
                  disabled={isDisabled}
                  title={title}
                >
                  {idx + 1}
                </Button>
              );
            })}
          </div>
          <Button
            variant="outline"
            size="sm"
            className="h-8 gap-1.5 text-xs"
            onClick={onClearAllImages}
            title="Clear all images"
          >
            <Eraser className="h-4 w-4" />
            <span className="hidden sm:inline">Clear</span>
          </Button>
          <ModeSelector
            activeMode={activeMode}
            modes={modes}
            onSelectMode={onSelectMode}
            onOpenModes={onOpenModes}
          />
          <Button
            variant="outline"
            size="sm"
            className="h-8 gap-1.5 text-xs"
            onClick={onOpenPresets}
            title="View Presets"
          >
            <BookMarked className="h-4 w-4" />
            <span className="hidden sm:inline">Presets</span>
          </Button>
        </div>
      </div>
    </div>
  );
}
